import {
  AnvilForm,
  FieldType,
} from 'components/anvilFormFillable/fields/types';
import { isEmptyString } from 'utils/helper';

export type StableKeySource = {
  formId?: number | null;
  id?: string | null;
  aliasId?: string | null;
  name?: string | null;
  title?: string | null;
  type?: string | null;
  pageNum?: number | null;
  rect?: { x?: number; y?: number; width?: number; height?: number } | null;
  eId?: string | null;
  formPdf?: { list?: { url?: { value?: string | null } | null }[] | null } | null;
};

export function getStableKey(
  item: StableKeySource,
  fallbackPrefix: string | null = 'item',
) {
  const prefix = fallbackPrefix ?? 'item';
  const rect = item.rect as
  { x?: number; y?: number; width?: number; height?: number } | null | undefined;

  if (item.formId != null && item.id) {
    return `${item.formId}:${item.id}`;
  }

  return item.id
    ?? item.aliasId
    ?? item.name
    ?? item.title
    ?? item.eId
    ?? item.formPdf?.list?.[0]?.url?.value
    ?? `${prefix}-${item.type ?? 'item'}-${item.pageNum ?? 'page'}-${rect ? `${rect.x ?? ''}-${rect.y ?? ''}-${rect.width ?? ''}-${rect.height ?? ''}` : ''}`;
}

/**
 * Calculates the absolute style properties (position, size, etc.)
 * for a form field based on its rectangle coordinates within a PDF page.
 *
 * @param field - A normalized form field with rect coordinates and page number.
 * @param pageHeight - The parent form pdf page height.
 * @param scaleFactor - The scaleFactor is the ratio of the container width to the page width.
 * @returns CSS style object to absolutely position the field on the rendered document.
 */
export function getStyleFromRect(
  field: AnvilForm,
  pageHeight: number,
  scaleFactor: number,
  isSignField?: boolean,
) {
  const { rect, pageNum: rawPageNum } = field;
  // Parse the page number from string or undefined, defaulting to 0
  const pageNum = Number(rawPageNum ?? 0);
  // Scale the horizontal (x) position of the field
  const left = (rect?.x ?? 0) * scaleFactor;
  // Calculate the vertical (y) position relative to the full document height
  const topOffset = (pageHeight - (rect?.y ?? 0) + (pageNum * pageHeight)) * scaleFactor;

  // Add vertical padding to separate pages visually
  // (pageNum * 66 is the height of the page gap + 24 is the top padding for the file name)
  const verticalPadding = (pageNum * 63) + 24;
  // Final top position after padding
  const top = topOffset + verticalPadding;

  // Scale the width and height of the field rectangle
  const width = (rect?.width ?? 0) * scaleFactor;
  const height = (rect?.height ?? 0) * scaleFactor;

  // Return the computed style object for absolute positioning
  return {
    position: 'absolute' as const,
    top,
    left,
    width,
    height: height + (isSignField ? 1 : 0),
    padding: isSignField ? 0 : 2,
    backgroundColor: '#f7f8fc',
    border: '1px solid #bee7f3',
    color: '#527490',
    boxSizing: 'border-box' as const,
  };
}

export function getFieldTypeByField(field: AnvilForm) {
  let fieldType = field?.type;

  if (fieldType === 'shortText' && !isEmptyString(field?.name)) {
    if (/ssn/i.test(field?.format ?? '')) {
      fieldType = 'ssn';
    } else if (/ein/i.test(field?.format ?? '')) {
      fieldType = 'ein';
    }
  }
  return fieldType as FieldType;
}

export function getFieldVerticalAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'bottom':
      return 'flex-end';
    case 'middle':
    case 'center':
      return 'center';
    default:
      return 'flex-start';
  }
}

export function getFieldHorizontalAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'center':
    case 'middle':
      return 'center';
    case 'right':
      return 'flex-end';
    default:
      return 'flex-start';
  }
}

export function getFieldTextAlignment(alignment?: string | null) {
  switch ((alignment ?? '').trim().toLowerCase()) {
    case 'center':
    case 'middle':
      return 'center';
    case 'right':
      return 'right';
    default:
      return 'left';
  }
}

export const placeholderByType: Partial<Record<FieldType, string>> = Object.freeze({
  fullName: 'Please enter name',
  email: 'Please enter email (e.g: hello@example.com)',
  usAddress: 'Please enter address',
  number: 'Please enter a number (e.g: 123)',
  percent: 'Please enter percentage value',
  integer: 'Please enter a number (e.g: 123)',
  ein: 'Please enter EIN (e.g: 99-8888887)',
  ssn: 'Please enter SSN (e.g: 5555-44-3333)',
});

const SIGNATURE_FAMILY_FIELD_TYPES = new Set([
  'signature',
  'initial',
  'signatureDate',
  'signatureTimestamp',
]);

export type FormFieldRequiredSource = Pick<Partial<AnvilForm>, 'isRequired' | 'type'>;

export function isFormFieldRequired(
  field?: FormFieldRequiredSource,
): boolean {
  return SIGNATURE_FAMILY_FIELD_TYPES.has(field?.type ?? '')
    || field?.isRequired !== false;
}

export function getFieldDisplayLabel(
  field?: { name?: string | null; type?: string | null },
): string | undefined {
  if (field?.type === 'checkbox') {
    return undefined;
  }
  if (isEmptyString(field?.name)) {
    return field?.type === 'radioGroup' || field?.type === 'checkboxGroup'
      ? 'Please Select One'
      : undefined;
  }
  return field?.name ?? undefined;
}
